import React, { useState } from "react";
import { TextT, Sparkle, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ModelSelector } from "../components/ModelSelector";

export default function TitleStudio() {
  const { setCredits } = useAuth();
  const [topic, setTopic] = useState("");
  const [count, setCount] = useState(5);
  const [model, setModel] = useState(null);
  const [compare, setCompare] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!topic.trim()) { toast.error("Add a topic"); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/titles", { topic, count, model, compare });
      if (typeof data?._credits_remaining === "number") setCredits(data._credits_remaining);
      setResult(data);
      toast.success(data.compare ? "Compared across models" : "Titles generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="title-studio">
      <p className="text-[#FF3B6B] text-sm font-mono">// TITLE & HOOK GENERATOR</p>
      <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2">Click-worthy titles, batched.</h1>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 glass rounded-xl p-6 space-y-5">
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider">Topic</label>
            <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. how to save $1000 in 30 days" className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-[#FF3B6B] focus:outline-none" data-testid="title-topic-input" />
          </div>
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider">Variations</label>
            <input type="number" min={3} max={10} value={count} onChange={(e) => setCount(Number(e.target.value))} className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#FF3B6B] focus:outline-none" data-testid="title-count-input" />
          </div>
          <ModelSelector type="text" action="titles" model={model} compare={compare} onChange={({ model: m, compare: c }) => { setModel(m); setCompare(c); }} />
          <button onClick={submit} disabled={loading} className="w-full btn-primary py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="generate-titles-btn">
            {loading ? (compare ? "Comparing 3 models..." : "Generating...") : <><Sparkle weight="fill" size={16} /> {compare ? "Compare across models" : "Generate titles"}</>}
          </button>
        </div>

        <div className="lg:col-span-7 space-y-3">
          {loading && (
            <div className="glass rounded-xl p-12 text-center" data-testid="titles-loading-state">
              <div className="inline-flex gap-2 items-center text-[#FF3B6B]">
                <span className="w-2 h-2 rounded-full bg-[#FF3B6B] animate-pulse" />
                <span className="w-2 h-2 rounded-full bg-[#FF3B6B] animate-pulse" style={{ animationDelay: "0.15s" }} />
                <span className="w-2 h-2 rounded-full bg-[#FF3B6B] animate-pulse" style={{ animationDelay: "0.3s" }} />
              </div>
              <p className="mt-4 text-white/70">{compare ? "Running 3 models in parallel..." : "Generating..."}</p>
              {compare && <p className="mt-1 text-xs text-white/40">Hang tight — usually 30–45 seconds</p>}
            </div>
          )}
          {!result && !loading && (
            <div className="glass rounded-xl p-12 text-center text-white/40 border-dashed">
              <TextT size={40} className="mx-auto mb-3 text-white/20" />
              <p>Variations will appear here</p>
            </div>
          )}
          {result && !result.compare && <TitleList titles={result.titles || []} />}
          {result?.compare && result.variants.map((v) => (
            <div key={v.model} className="space-y-2" data-testid={`variant-${v.model}`}>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-xs font-mono px-2 py-1 rounded-full bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30">{v.label}</span>
                {v.error && <span className="text-xs text-red-400">failed: {v.error}</span>}
              </div>
              {v.data && <TitleList titles={v.data.titles || []} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const TitleList = ({ titles }) => (
  <>
    {titles.map((t, i) => (
      <div key={i} className="glass rounded-xl p-4 flex items-center justify-between gap-4 hover:border-white/30 transition-all" data-testid={`title-result-${i}`}>
        <p className="text-white/90 font-medium flex-1">{t.title}</p>
        <div className="flex items-center gap-3 shrink-0">
          <div className="font-mono text-sm">
            <span className="text-[#FF3B6B] font-bold">{t.hook_score}</span>
            <span className="text-white/40">/100</span>
          </div>
          <button onClick={() => { navigator.clipboard.writeText(t.title); toast.success("Copied"); }} className="text-white/40 hover:text-white"><Copy size={14} /></button>
        </div>
      </div>
    ))}
  </>
);
